import React, { useState, useEffect, useRef } from 'react';
import { api, fileToBase64 } from '../../lib/api';
import { useApp } from '../../context/AppContext';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Progress } from '../ui/progress';
import { ScanLine, Upload, Camera, Trash2, Pencil, Check, Flame as FlameIcon } from 'lucide-react';
import { toast } from 'sonner';

const MACRO_COLORS = {
  protein: 'hsl(142 92% 52%)',
  carbs: 'hsl(205 100% 56%)',
  fats: 'hsl(38 92% 55%)',
};

export default function FoodScannerTab() {
  const { t } = useApp();
  const [preview, setPreview] = useState(null);
  const [scanning, setScanning] = useState(false);
  const [result, setResult] = useState(null);
  const [journal, setJournal] = useState({ entries: [], totals: { calories: 0, protein_g: 0, carbs_g: 0, fats_g: 0 } });
  const [editingId, setEditingId] = useState(null);
  const [editVals, setEditVals] = useState({});
  const fileRef = useRef();
  const cameraRef = useRef();

  const loadJournal = () => api.get('/ai/food/journal').then((r) => setJournal(r.data)).catch(() => {});
  useEffect(() => { loadJournal(); }, []);

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 5 * 1024 * 1024) { toast.error('Image too large (max 5MB)'); return; }
    const b64 = await fileToBase64(f);
    setPreview(b64);
    setResult(null);
    e.target.value = '';
  };

  const scan = async () => {
    if (!preview) return;
    setScanning(true);
    try {
      const res = await api.post('/ai/food/scan', { image_base64: preview });
      setResult(res.data);
      toast.success(`Detected: ${res.data.food_name}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Scan failed');
    } finally { setScanning(false); }
  };

  const addToJournal = async () => {
    if (!result) return;
    try {
      await api.post('/ai/food/journal', {
        food_name: result.food_name,
        portion: result.portion_estimate,
        calories: result.macros.calories,
        protein_g: result.macros.protein_g,
        carbs_g: result.macros.carbs_g,
        fats_g: result.macros.fats_g,
      });
      toast.success('Logged to your food journal!');
      setResult(null); setPreview(null);
      loadJournal();
    } catch { toast.error('Failed to log'); }
  };

  const startEdit = (entry) => {
    setEditingId(entry.id);
    setEditVals({ calories: entry.calories, protein_g: entry.protein_g, carbs_g: entry.carbs_g, fats_g: entry.fats_g });
  };

  const saveEdit = async (id) => {
    try {
      await api.put(`/ai/food/journal/${id}`, {
        calories: Number(editVals.calories), protein_g: Number(editVals.protein_g),
        carbs_g: Number(editVals.carbs_g), fats_g: Number(editVals.fats_g),
      });
      setEditingId(null);
      loadJournal();
      toast.success('Updated');
    } catch { toast.error('Update failed'); }
  };

  const remove = async (id) => {
    try { await api.delete(`/ai/food/journal/${id}`); loadJournal(); } catch {}
  };

  return (
    <div className="space-y-5">
      {/* Scanner */}
      <div className="bg-card rounded-xl border border-border p-5 elev-1">
        <h3 className="font-semibold mb-4 flex items-center gap-2">
          <ScanLine size={17} className="text-[hsl(205,100%,56%)]" /> {t('scanMeal')}
        </h3>
        <input ref={fileRef} type="file" accept="image/*" hidden onChange={onFile} />
        <input ref={cameraRef} type="file" accept="image/*" capture="environment" hidden onChange={onFile} />
        {!preview ? (
          <div className="border-2 border-dashed border-[hsl(205,100%,56%)]/30 rounded-xl p-8 text-center">
            <ScanLine size={32} className="mx-auto mb-3 text-[hsl(205,100%,56%)]/60" />
            <p className="text-sm text-muted-foreground mb-4">Take a photo of your meal and AI will estimate the macros</p>
            <div className="flex gap-2 justify-center">
              <Button data-testid="food-scanner-camera-button" variant="outline" onClick={() => cameraRef.current?.click()} className="border-[hsl(205,100%,56%)]/40 text-[hsl(205,100%,56%)]">
                <Camera size={15} className="me-2" /> Camera
              </Button>
              <Button data-testid="food-scanner-upload-button" variant="outline" onClick={() => fileRef.current?.click()} className="border-[hsl(205,100%,56%)]/40 text-[hsl(205,100%,56%)]">
                <Upload size={15} className="me-2" /> Upload
              </Button>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="relative rounded-xl overflow-hidden">
              <img src={preview} alt="meal" className="w-full max-h-72 object-cover" />
              {scanning && (
                <div className="absolute inset-0 bg-black/60 flex flex-col items-center justify-center gap-3">
                  <div className="w-8 h-8 border-2 border-[hsl(205,100%,56%)] border-t-transparent rounded-full animate-spin" />
                  <p className="text-white text-sm">AI analyzing your meal...</p>
                </div>
              )}
            </div>
            {!result && (
              <div className="flex gap-2">
                <Button data-testid="food-scanner-scan-button" onClick={scan} disabled={scanning}
                  className="flex-1 bg-[hsl(205,100%,56%)] text-black hover:bg-[hsl(205,100%,62%)] font-semibold">
                  {scanning ? 'Analyzing...' : `${t('scanMeal')} (AI)`}
                </Button>
                <Button variant="outline" onClick={() => { setPreview(null); setResult(null); }}>{t('cancel')}</Button>
              </div>
            )}
          </div>
        )}

        {/* Result */}
        {result && (
          <div className="mt-4 p-4 rounded-xl border border-[hsl(205,100%,56%)]/30 bg-[hsl(205,100%,56%)]/5 glow-ai" data-testid="food-scanner-results-macros">
            <div className="flex items-center justify-between mb-3">
              <div>
                <h4 className="font-semibold">{result.food_name}</h4>
                <p className="text-xs text-muted-foreground">{result.portion_estimate} · confidence {Math.round((result.confidence || 0) * 100)}%</p>
              </div>
              <div className="text-end">
                <p className="text-2xl font-bold text-[hsl(205,100%,56%)]">{result.macros.calories}</p>
                <p className="text-[10px] text-muted-foreground uppercase">kcal</p>
              </div>
            </div>
            <div className="grid grid-cols-3 gap-3 mb-3">
              {[['protein', result.macros.protein_g], ['carbs', result.macros.carbs_g], ['fats', result.macros.fats_g]].map(([k, v]) => (
                <div key={k} className="text-center p-2 rounded-lg bg-secondary/60">
                  <p className="text-sm font-semibold" style={{ color: MACRO_COLORS[k] }}>{v}g</p>
                  <p className="text-[10px] text-muted-foreground">{t(k)}</p>
                </div>
              ))}
            </div>
            {result.health_note && <p className="text-xs text-muted-foreground mb-3">{result.health_note}</p>}
            <Button data-testid="food-journal-add-entry-button" onClick={addToJournal} className="w-full font-semibold">
              {t('addToJournal')}
            </Button>
          </div>
        )}
      </div>

      {/* Journal */}
      <div className="bg-card rounded-xl border border-border p-5 elev-1" data-testid="food-journal-section">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-semibold flex items-center gap-2">
            <FlameIcon size={17} className="text-primary" /> {t('foodJournal')}
          </h3>
          <span className="text-sm font-semibold text-primary">{Math.round(journal.totals.calories)} kcal</span>
        </div>
        <div className="grid grid-cols-3 gap-3 mb-4">
          {[['protein', journal.totals.protein_g, 150], ['carbs', journal.totals.carbs_g, 250], ['fats', journal.totals.fats_g, 70]].map(([k, v, max]) => (
            <div key={k}>
              <div className="flex justify-between text-[11px] mb-1">
                <span className="text-muted-foreground">{t(k)}</span>
                <span style={{ color: MACRO_COLORS[k] }}>{Math.round(v)}g</span>
              </div>
              <Progress value={Math.min(100, (v / max) * 100)} className="h-1.5" />
            </div>
          ))}
        </div>
        <div className="space-y-2">
          {journal.entries.map((e) => (
            <div key={e.id} className="p-3 rounded-lg bg-secondary/50 border border-border" data-testid="food-journal-entry">
              {editingId === e.id ? (
                <div className="space-y-2">
                  <p className="text-sm font-medium">{e.food_name}</p>
                  <div className="grid grid-cols-4 gap-2">
                    {['calories', 'protein_g', 'carbs_g', 'fats_g'].map((k) => (
                      <Input key={k} type="number" value={editVals[k]} onChange={(ev) => setEditVals({ ...editVals, [k]: ev.target.value })} className="h-8 text-xs" />
                    ))}
                  </div>
                  <Button data-testid="food-journal-save-edit-button" size="sm" className="h-7 text-xs" onClick={() => saveEdit(e.id)}>
                    <Check size={12} className="me-1" /> {t('save')}
                  </Button>
                </div>
              ) : (
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium">{e.food_name}</p>
                    <p className="text-[11px] text-muted-foreground">
                      {Math.round(e.calories)} kcal · P {e.protein_g}g · C {e.carbs_g}g · F {e.fats_g}g
                    </p>
                  </div>
                  <div className="flex gap-1">
                    <button data-testid="food-journal-edit-button" onClick={() => startEdit(e)} className="p-1.5 rounded hover:bg-secondary text-muted-foreground"><Pencil size={13} /></button>
                    <button data-testid="food-journal-delete-button" onClick={() => remove(e.id)} className="p-1.5 rounded hover:bg-secondary text-muted-foreground hover:text-destructive"><Trash2 size={13} /></button>
                  </div>
                </div>
              )}
            </div>
          ))}
          {journal.entries.length === 0 && (
            <p className="text-center text-xs text-muted-foreground py-4">No meals logged today. Scan your first meal!</p>
          )}
        </div>
      </div>
    </div>
  );
}
